// Decides which pricing table the Billing tab draws for a given billing model, and what
// goes in it.
//
// This is the presentation twin of the PerSeatBilling core on the Ruby side: a pure
// function, no React, no network, no form state. It takes a billing model and the
// company's billing rows and returns a description of the table — nothing else in here
// knows how to render. That keeps the surface rules unit-testable on their own and keeps
// Billing.jsx down to wiring the description onto elements.
//
// Three surfaces exist:
//
//   vehicle  the legacy split models, one row per car type over all nine distance bands
//   tiered   Per Seat - Tiered, ONE row over the same nine bands
//   fixed    Per Seat - Fixed, ONE row over a single "Any Distance" column

// The nine distance bands every CompanyBilling row carries. Mirrors
// PerSeatBilling::DistanceBand on the Ruby side — if a band boundary ever moves, both
// change together.
export const TIER_COLUMNS = [
    { label: "0 - 19km", key: "km0_km19" },
    { label: "20 - 29km", key: "km20_km29" },
    { label: "30 - 39km", key: "km30_km39" },
    { label: "40 - 49km", key: "km40_km49" },
    { label: "50 - 59km", key: "km50_km59" },
    { label: "60 - 69km", key: "km60_km69" },
    { label: "70 - 79km", key: "km70_km79" },
    { label: "80 - 89km", key: "km80_km89" },
    { label: "90km +", key: "km90" },
];

// Present on all three surfaces, always at the end of the row.
export const TAIL_COLUMNS = [
    { label: "Cancelled", key: "cancelled" },
    { label: "App Fee", key: "app_fee" },
    { label: "Vat %", key: "vat_percentage" },
];

// Per Seat - Fixed bills one rate at any distance, so the nine bands collapse to a single
// column. It still writes km0_km19 — the save handler fans that value across every band
// and every vehicle row, which is what keeps the invoice and payment matrix readable.
export const ANY_DISTANCE_COLUMN = { label: "Any Distance", key: "km0_km19", testKey: "any_distance" };

// Fixed only. A solo ride under a plain fixed rate costs one seat, which prices the whole
// vehicle at the price of a single share — these two let a company charge a solo ride its own
// amount instead, split by which scheduler booked it. Mirrors
// PerSeatBilling::Pricing::Fixed on the Ruby side. Left of the tail columns, so they sit
// before Cancelled.
//
// Leaving a cell empty is meaningful: pricing falls back to the fixed rate, so an existing
// Fixed company bills exactly as it does today until someone fills these in.
export const SOLO_TRIP_COLUMNS = [
    {
        label: "Single Trip",
        key: "single_trip",
        tooltip: "Grouped trips booked through the group trip scheduler (Team Manager → "
            + "Scheduler tab) that end up carrying only one passenger. Charged instead of the "
            + "Any Distance rate. Leave empty to keep charging the Any Distance rate."
    },
    {
        label: "Adhoc Trip",
        key: "adhoc_trip",
        tooltip: "Grouped trips booked for a single user through the Ad-hoc scheduler (Team "
            + "Manager → Ad-hocs tab), carrying only one passenger. Charged instead of the Any "
            + "Distance rate. Leave empty to keep charging the Any Distance rate."
    }
];

export const CAR_TYPE_NAMES = {
    premium_sedan: "Premium Sedan",
    van: "Van",
    minibus: "Minibus",
    hatchback: "Hatchback"
};

const VEHICLE_CAR_TYPES = Object.keys(CAR_TYPE_NAMES);

// Anything not listed here is a legacy split model and prices per vehicle type.
const KIND_BY_BILLING_MODEL = {
    per_seat_tiered: 'tiered',
    per_seat_fixed: 'fixed'
};

const COPY = {
    vehicle: {
        title: 'Vehicle Pricing',
        subtitle: 'Pricing per vehicle type',
        note: null
    },
    tiered: {
        title: 'Per Seat Pricing: Tiered',
        subtitle: 'One cost per employee, set by how far that employee travels',
        // Deliberately says "the site that trip serves" rather than "work": the tier is read
        // off the booking's own routed leg (trips.distance), not off the employee's stored
        // distance to the company's primary address, so a multi-site company bills each site
        // on its own distance. See per_seat_billing_fee_semantics.md decision 0.
        note: 'Each employee is billed at the tier their own trip distance falls in — from '
            + 'home to the site that trip serves. An employee 25km from that site on a R300 '
            + 'tier costs R300 for that trip, and colleagues sharing the ride are billed at '
            + 'their own tiers. The vehicle and the ride\'s total distance do not change it.'
    },
    fixed: {
        title: 'Per Seat Pricing: Fixed',
        subtitle: 'One cost per employee, any distance',
        note: 'Every employee is billed the same amount per trip, whatever the distance or '
            + 'vehicle: 4 passengers on a R300 fixed cost bill R1 200. The value saves to every '
            + 'tier and vehicle type. A ride carrying only ONE passenger is billed from Single '
            + 'Trip or Adhoc Trip instead, depending on which scheduler booked it — those are '
            + 'charged in place of the Any Distance rate, never on top of it.'
    }
};

// Every vehicle row carries identical values once the save handler has fanned a per-seat
// rate out, so the single visible row reads and writes the first row and the handler
// propagates it to the rest.
const PER_SEAT_ROW = {
    tiered: { key: 'tiered_cost', label: 'Tiered Cost' },
    fixed: { key: 'fixed', label: 'Fixed' }
};

/**
 * @param {string} kind one of 'vehicle', 'tiered', 'fixed'
 * @returns {Array<{label: string, key: string, testKey?: string}>} the editable columns
 */
const columnsFor = (kind) => (
    kind === 'fixed'
        ? [ANY_DISTANCE_COLUMN, ...SOLO_TRIP_COLUMNS, ...TAIL_COLUMNS]
        : [...TIER_COLUMNS, ...TAIL_COLUMNS]
);

/**
 * @param {string} kind one of 'vehicle', 'tiered', 'fixed'
 * @param {Array<Object>} companyBillings the company's parsed billing rows
 * @returns {Array<{key: string, label: string, billingIndex: number}>} the table's rows
 */
const rowsFor = (kind, companyBillings) => {
    if (kind !== 'vehicle') {
        return [{ ...PER_SEAT_ROW[kind], billingIndex: 0 }];
    }

    return VEHICLE_CAR_TYPES.map((carType) => ({
        key: carType,
        label: CAR_TYPE_NAMES[carType],
        billingIndex: companyBillings.findIndex((billing) => billing.car_type == carType)
    }));
};

/**
 * Describes the pricing table for a billing model.
 *
 * @param {string} billingModel the Company#billing_model value
 * @param {Array<Object>} companyBillings the company's parsed billing rows
 * @returns {{
 *   kind: string,
 *   isPerSeat: boolean,
 *   title: string,
 *   subtitle: string,
 *   note: ?string,
 *   leadColumnLabel: string,
 *   columns: Array<Object>,
 *   rows: Array<Object>
 * }}
 */
export const pricingSurfaceFor = (billingModel, companyBillings = []) => {
    const kind = KIND_BY_BILLING_MODEL[billingModel] || 'vehicle';
    const isPerSeat = kind !== 'vehicle';

    return {
        kind,
        isPerSeat,
        ...COPY[kind],
        leadColumnLabel: isPerSeat ? 'Cost Type' : 'Car Type',
        columns: columnsFor(kind),
        rows: rowsFor(kind, companyBillings)
    };
};
